import { negotiateLocale } from '@yourstore/domain';

/**
 * Which language this console's own words are in.
 *
 * ## The rule for this surface, and why it is the shortest one in the system
 *
 * The operator console is **cross-tenant by definition** (spec §6.3): the fleet
 * screen is about every business at once and the tenant page is about whichever
 * one is open thirty seconds later. So there is no tenant whose `default_locale`
 * could possibly apply — the seat belongs to whoever is sitting in it, not to a
 * business. The guest site's ladder (`?locale=` → `Accept-Language` →
 * `clients.locale` → the tenant's default, docs/i18n-design.md §3) has nothing
 * to negotiate here, because three of its four rungs are properties of a guest
 * or a tenant and this screen has neither.
 *
 * The language therefore comes from **the operator's own browser**
 * (`navigator.languages`), narrowed to the catalog set, and resolved **once at
 * module load** (`index.tsx`). That is the same rule the staff PWA follows, for
 * a different reason arriving at the same place: there, a counter iPad belongs
 * to one business and the device *is* the person; here, the seat belongs to no
 * business and the device is again the only honest signal.
 *
 * Resolved once rather than per render because it cannot change without a page
 * load: `navigator.languages` is fixed for the life of the document, and a hook
 * that re-read it every render would be a hook that always answered the same
 * thing while implying it might not.
 *
 * The negotiation itself is `@yourstore/domain`'s. There is exactly one ladder
 * in this system for the same reason there is one scheduling engine, and
 * `negotiateLocale` is it — `es-419`, `es-MX` and `ES` all land on `es` without
 * this file knowing how.
 */

/** Every language the UI catalogs cover. Adding one is a file, not a refactor. */
export const UI_LOCALES = ['en', 'es'] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

/**
 * English, because it is the USVI's official language and because an operator
 * who has expressed no preference at all is reading it (§1).
 */
export const DEFAULT_UI_LOCALE: UiLocale = 'en';

/**
 * Narrow anything BCP-47 to a language this console actually speaks.
 *
 * Preferences are given most-wanted first. A tag we have no catalog for —
 * `fr-HT`, the designed-for third — falls to English rather than to a gap: a
 * Haitian operator reads an English console today and a French one the day
 * `fr.ts` exists, with nothing else changing.
 */
export function narrowUiLocale(...preferences: readonly (string | null | undefined)[]): UiLocale {
  const picked = negotiateLocale(preferences, UI_LOCALES, DEFAULT_UI_LOCALE);
  // `negotiateLocale` answers with a member of `supported`, but proving that to
  // the compiler with a cast would be asserting rather than checking.
  return UI_LOCALES.find((locale) => locale === picked) ?? DEFAULT_UI_LOCALE;
}

/**
 * What the browser says it reads, most-wanted first.
 *
 * `navigator.languages` rather than `navigator.language`, because an operator
 * whose first choice we have no catalog for may well have a second we do.
 * Guarded because this bundle is also rendered in tests and by a jsdom that may
 * have neither.
 */
export function browserLocales(): string[] {
  const nav: { languages?: readonly string[]; language?: string } | undefined =
    globalThis.navigator as { languages?: readonly string[]; language?: string } | undefined;
  if (nav === undefined) return [];
  if (Array.isArray(nav.languages)) return [...nav.languages];
  return typeof nav.language === 'string' ? [nav.language] : [];
}
